import { useEffect, useRef } from 'react';

const PARTICLE_COLORS = [
  '#fa4f6a', // Pink
  '#cdf1fc', // Light Blue
  '#e03c57', // Red
  '#ffffff'  // White
];

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  depth: number;
}

export const InteractiveBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };

    const initParticles = () => {
      particles = [];
      const particleCount = Math.min(100, (canvas.width * canvas.height) / 15000);
      
      for (let i = 0; i < particleCount; i++) {
        particles.push(createParticle());
      }
    };

    const createParticle = (): Particle => {
      const depth = Math.random() * 0.8 + 0.2;
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: (Math.random() * 20 + 10) * depth,
        speedX: (Math.random() - 0.5) * 1 * depth,
        speedY: (Math.random() - 0.5) * 1 * depth,
        color: PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)],
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.05,
        depth
      };
    };

    const drawCube = (ctx: CanvasRenderingContext2D, p: Particle) => {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      
      ctx.fillStyle = p.color;
      ctx.globalAlpha = 0.8 * p.depth;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
      
      ctx.fillStyle = '#000000';
      ctx.globalAlpha = 0.2 * p.depth;
      ctx.fillRect(-p.size / 2, -p.size / 2 + p.size * 0.2, p.size * 0.2, p.size);
      ctx.fillRect(-p.size / 2, -p.size / 2 + p.size - p.size * 0.2, p.size, p.size * 0.2);

      ctx.restore();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.rotation += p.rotationSpeed;

        const dx = p.x - mouseRef.current.x;
        const dy = p.y - mouseRef.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const interactionRadius = 300; 

        if (distance < interactionRadius) {
          const force = (interactionRadius - distance) / interactionRadius;
          const angle = Math.atan2(dy, dx);
          const pushStrength = 15;
          const push = force * pushStrength * p.depth; 
          
          p.x += Math.cos(angle) * push;
          p.y += Math.sin(angle) * push;
        }

        if (p.x < -50) p.x = canvas.width + 50;
        if (p.x > canvas.width + 50) p.x = -50;
        if (p.y < -50) p.y = canvas.height + 50;
        if (p.y > canvas.height + 50) p.y = -50;

        drawCube(ctx, p);
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    
    resize();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0 z-0 overflow-hidden bg-[#121212] pointer-events-none">
      <div 
        className="absolute inset-0 opacity-[0.05]" 
        style={{
          backgroundImage: `
            linear-gradient(to right, #888 1px, transparent 1px),
            linear-gradient(to bottom, #888 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          transform: 'perspective(500px) rotateX(20deg) scale(1.5)',
          transformOrigin: 'center 0',
        }}
      ></div>
      
      <canvas ref={canvasRef} className="block w-full h-full pointer-events-none" />
      
      {/* Bottom fade to blend into #121212 */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-dark via-dark/80 to-transparent"></div>
    </div>
  );
};

export default InteractiveBackground;
